/* Decorative, one-time count-up. The accurate final values remain in the HTML. */
(() => {
  'use strict';

  const marker = 'data-ial-counters-initialized';
  if (document.documentElement.hasAttribute(marker)) return;
  document.documentElement.setAttribute(marker, '');

  const duration = 1100;
  const active = new Map();
  const completed = new WeakSet();
  let intersectionObserver;
  let motion;

  try {
    motion = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  } catch (_) {
    motion = null;
  }

  const canAnimate = Boolean(
    motion &&
    (typeof motion.addEventListener === 'function' || typeof motion.addListener === 'function') &&
    typeof window.IntersectionObserver === 'function' &&
    typeof window.MutationObserver === 'function' &&
    typeof window.requestAnimationFrame === 'function' &&
    typeof window.cancelAnimationFrame === 'function'
  );

  const format = (value) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  function finish(record) {
    if (record.frame !== null) window.cancelAnimationFrame(record.frame);
    if (intersectionObserver) intersectionObserver.unobserve(record.node);
    record.node.textContent = record.finalText;
    record.frame = null;
    completed.add(record.node);
    active.delete(record.node);
  }

  function animate(record, timestamp) {
    record.frame = null;
    if (!record.node.isConnected || motion.matches) {
      finish(record);
      return;
    }
    if (record.start === null) record.start = timestamp;
    const progress = Math.max(0, Math.min(1, (timestamp - record.start) / duration));
    if (progress === 1) {
      finish(record);
      return;
    }
    const value = Math.min(record.target, Math.floor(record.target * (1 - Math.pow(1 - progress, 3))));
    record.node.textContent = format(value);
    record.frame = window.requestAnimationFrame((time) => animate(record, time));
  }

  function observe(record) {
    if (!intersectionObserver) {
      intersectionObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const current = active.get(entry.target);
          if (!current) return;
          if (!current.node.isConnected || motion.matches) {
            finish(current);
          } else if (entry.isIntersecting && !current.started) {
            current.started = true;
            intersectionObserver.unobserve(current.node);
            current.frame = window.requestAnimationFrame((time) => animate(current, time));
          }
        });
      }, { threshold: 0.2 });
    }
    intersectionObserver.observe(record.node);
  }

  function initialize(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    scope.querySelectorAll('[data-ial-count]').forEach((node) => {
      if (!node.closest('.inopro-about-light') || active.has(node) || completed.has(node)) return;
      const raw = node.getAttribute('data-ial-count');
      if (!raw || !/^(0|[1-9]\d*)$/.test(raw)) return;
      const target = Number(raw);
      if (!Number.isSafeInteger(target)) return;
      const record = {
        node,
        target,
        finalText: node.textContent,
        start: null,
        frame: null,
        started: false
      };
      if (!canAnimate || motion.matches || target === 0) {
        completed.add(node);
        return;
      }
      active.set(node, record);
      observe(record);
    });
  }

  function stopForReducedMotion() {
    if (motion.matches) Array.from(active.values()).forEach(finish);
  }

  if (canAnimate) {
    if (typeof motion.addEventListener === 'function') {
      motion.addEventListener('change', stopForReducedMotion);
    } else {
      motion.addListener(stopForReducedMotion);
    }

    // Watch only element removals, not the text changes made by the animation.
    const removalObserver = new window.MutationObserver((mutations) => {
      if (!mutations.some((mutation) => Array.from(mutation.removedNodes).some((node) => node.nodeType === 1))) return;
      Array.from(active.values()).forEach((record) => {
        if (!record.node.isConnected) finish(record);
      });
    });
    removalObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(active.values()).forEach((record) => {
      if (event.target && typeof event.target.contains === 'function' && event.target.contains(record.node)) finish(record);
    });
  });

  initialize(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  }
})();
